import { useRef, useState, useMemo, useEffect, ChangeEvent } from 'react';
import countryList from 'country-list/data.json';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import {
  CardComponent,
  CardNumber,
  CardExpiry,
  CardCVV,
} from '@chargebee/chargebee-js-react-wrapper';
import { getChargebeeSiteAndKey, SelectedCurrency } from 'utils/chargebee';
import { subscribeCard, signupCompletedAndRedirectToDashboard } from 'v6/api/signup';
import { Input } from 'Components';
import { chargebeeConfig } from './chargebeeConfig';
import SuccessBlock from './SuccessBlock';
import { FormDataProps, CardFieldsError } from './types';
import { FormDataProps as StoreType } from 'utils/types';
import { PayPalButton } from './PayPalButton';
import { getIsMobile } from 'utils/isMobile';
import { Label } from 'Components/Common/styled';
import { ContactSection } from '../Homepage/Actions';
import { CouponForm } from './CouponForm';
import { BREAKPOINTS } from 'constants/common';
import { Alert } from 'v6/Components/Alert';
import { bugsnagNotify } from 'utils/error';

// detecting mobile device by screen size
const isMobile = getIsMobile();

export const computedCountryOptions = countryList
  .filter(({ code }) => !['AU', 'GB', 'US'].includes(code))
  .map(({ code, name }) => ({
    label: name,
    value: code,
  }))
  .sort((a, b) => {
    const nameA = a.label.toUpperCase(); // ignore upper and lowercase
    const nameB = b.label.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    // names must be equal
    return 0;
  });

export const countryOptions = [
  { label: 'Australia', value: 'AU' },
  { label: 'United Kingdom of Great Britain and Northern Ireland', value: 'GB' },
  { label: 'United States of America', value: 'US' },
  { label: '———————————————————————', options: computedCountryOptions },
];

interface PaymentFormProps {
  coupon: string | undefined;
  couponAdded: boolean;
  chargebeeError: string;
  setChargebeeError: (val: string) => void;
  setCoupon: (coupon: string) => void;
  setIsSubscribed: (value: boolean) => void;
  isSubscribed?: boolean;
  isSubmittingForm?: boolean;
  setIsSubmittingForm: (value: boolean) => void;
  setIsFormValid: (value: boolean) => void;
  control: any;
  errors: any;
  isValidForm: any;
  getValues: any;
  setOnSubmit: (object: any) => void;
}

const PaymentForm = (props: PaymentFormProps) => {
  const {
    coupon = '',
    couponAdded = false,
    chargebeeError = '',
    setChargebeeError,
    setCoupon,
    setIsSubscribed,
    isSubscribed,
    setIsSubmittingForm,
    isSubmittingForm,
    setIsFormValid,
    control,
    errors,
    isValidForm,
    setOnSubmit,
  } = props;

  const history = useHistory();
  const { state } = useStateMachine();
  const defaultValuesFromStorage = state as StoreType;
  const { country, user, currency, billingPeriod, numberAccounts } = defaultValuesFromStorage;
  const selectedPlanId = defaultValuesFromStorage.selectedPlanId;
  const cardRef = useRef<any>(null);
  const [cbInstance, setCbInstance] = useState(null);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  const [cardNumberFormFieldsError, setCardNumberFormFieldsError] = useState<CardFieldsError>({
    error: '',
    completed: false,
  });
  const [cardExpiryFormFieldsError, setCardExpiryFormFieldsError] = useState<CardFieldsError>({
    error: '',
    completed: false,
  });
  const [cardCvvFormFieldsError, setCardCvvFormFieldsError] = useState<CardFieldsError>({
    error: '',
    completed: false,
  });

  useEffect(() => {
    if (!currency) {
      return history.replace({ pathname: '/plan' });
    }

    const { site, key } = getChargebeeSiteAndKey(currency as SelectedCurrency);
    const instance = (window as any).Chargebee.init({
      site,
      publishableKey: key,
    });

    setCbInstance(instance);
    setOnSubmit(() => onSubmit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardFormFirstError = useMemo(() => {
    const fieldError = [
      cardNumberFormFieldsError,
      cardExpiryFormFieldsError,
      cardCvvFormFieldsError,
    ].find((field: any) => field.error);

    return fieldError ? fieldError.error : '';
  }, [cardCvvFormFieldsError, cardExpiryFormFieldsError, cardNumberFormFieldsError]);

  useEffect(() => {
    setIsFormValid(
      isValidForm &&
        [cardNumberFormFieldsError, cardExpiryFormFieldsError, cardCvvFormFieldsError].every(
          (field: any) => field.error.length === 0 && field.completed,
        ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidForm, cardCvvFormFieldsError, cardExpiryFormFieldsError, cardNumberFormFieldsError]);

  const handleCardFieldChange = ({ field, complete, error }: any) => {
    switch (field) {
      case 'number':
        setCardNumberFormFieldsError({
          error: error?.message
            ? 'Invalid or incorrect card details. Please double-check your details and try again.'
            : '',
          completed: complete,
        });
        break;
      case 'expiry':
        setCardExpiryFormFieldsError({
          error: error?.message || '',
          completed: complete,
        });
        break;
      case 'cvv':
        setCardCvvFormFieldsError({
          error: error?.errorCode === 'card_cvv_invalid' ? 'Incorrect CVC' : error?.message || '',
          completed: complete,
        });
        break;
      default:
        break;
    }
  };

  const onSubmit = (formData: FormDataProps) => {
    if (!cardRef?.current) return;

    setIsSubmittingForm(true);
    // https://www.chargebee.com/checkout-portal-docs/components-fields-reference.html#chargebee-instance-object
    // uses country entered by user in first step, no need to re-collect
    cardRef?.current
      ?.tokenize({ firstName: formData.nameOnCard, countryCode: country })
      .then(async (data: any) => {
        // send card token and selected plan to server
        await subscribeCard(
          user._id,
          data.token,
          selectedPlanId,
          couponAdded ? [coupon] : null,
          currency,
          billingPeriod,
          numberAccounts,
        );

        if (isMobile) {
          // if mobile redirect to special page
          history.push({ pathname: '/start-trial' });
        } else {
          setIsSubscribed(true);
          // if desktop just show success and redirect to home
          setTimeout(() => {
            signupCompletedAndRedirectToDashboard(selectedPlanId);
          }, 1000);
        }
      })
      .catch((error: any) => {
        console.log('tokenize error', error);
        bugsnagNotify(error, 'Error tokenizing card');

        setServerErrorMessage(
          `${error.status ? `(${error.status}) ` : ''}${
            error.status === 500
              ? `Sorry, something went wrong when trying to process the request. Please wait a few moments and try again, or contact our support team and we will assist.`
              : error.message
          }`,
        );
      })
      .finally(() => {
        setIsSubmittingForm(false);
      });
  };

  return (
    <>
      {isSubscribed ? (
        <SuccessBlock />
      ) : (
        <Form data-testid="paymentForm">
          {(cardFormFirstError || serverErrorMessage) && (
            <Alert type="error">{cardFormFirstError || serverErrorMessage}</Alert>
          )}
          <InputWrapper>
            <Controller
              control={control}
              name="nameOnCard"
              defaultValue=""
              rules={{
                required: 'Name on card is required.',
                minLength: 1,
                maxLength: 50,
              }}
              render={({ field: { value, onChange } }) => {
                let errorMsg = errors.nameOnCard?.message;
                if (errors.nameOnCard?.type === 'minLength') {
                  errorMsg = 'Name on card must be at least 1 character.';
                } else if (errors.nameOnCard?.type === 'maxLength') {
                  errorMsg = 'Name on card must be less than 50 characters.';
                }

                const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value?.trim() ? e.target.value : '');
                };

                return (
                  <StyledInput
                    data-testid="nameOnCardInput"
                    id="firstName"
                    type="text"
                    placeholder="Name on Card"
                    value={value}
                    onChange={handleNameChange}
                    error={errorMsg}
                  />
                );
              }}
            />
          </InputWrapper>
          {cbInstance && (
            <CardComponent
              ref={cardRef}
              styles={chargebeeConfig.style}
              placeholder={chargebeeConfig.placeholder}
              fonts={chargebeeConfig.fonts}
              onChange={handleCardFieldChange}
            >
              <CardNumber className="payment-details-input" />

              <Flex>
                <CardExpiry className="payment-details-input" />
                <CardCVV className="payment-details-input" />
              </Flex>
            </CardComponent>
          )}

          <OrText>or</OrText>

          {cbInstance && (
            <PayPalButton
              cbInstance={cbInstance}
              currency={currency}
              selectedPlanId={selectedPlanId}
              setError={setServerErrorMessage}
              _id={user._id}
              coupon_ids={couponAdded ? [coupon] : null}
              billingPeriod={billingPeriod}
              numberAccounts={numberAccounts}
            ></PayPalButton>
          )}

          {!isSubmittingForm && !isSubscribed && (
            <CouponForm
              coupon={coupon}
              couponAdded={couponAdded}
              chargebeeError={chargebeeError}
              setChargebeeError={setChargebeeError}
              setCoupon={setCoupon}
              plan={selectedPlanId}
              currency={currency}
            />
          )}

          <ContactSectionWrapDesktop>
            <ContactSection />
          </ContactSectionWrapDesktop>
        </Form>
      )}
    </>
  );
};

export default PaymentForm;

const Form = styled.form`
  width: 100%;
  margin-top: 20px;
`;

export const StyledLabel = styled(Label)`
  width: 100%;
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)``;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 18px;

  .payment-details-input {
    width: 50%;
  }
`;

const InputWrapper = styled.div`
  width: 100%;
  margin-bottom: 17px;
`;
const OrText = styled.div`
  color: var(--Text-T01, #333);

  /* Text sm/Regular */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  margin: 8px auto;
  text-align: center;
`;

const ContactSectionWrapDesktop = styled.div`
  display: block;
  @media (max-width: ${BREAKPOINTS.tablet}px) {
    display: none;
  }
`;
